import React, { useEffect, useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import makeStyles from '@mui/styles/makeStyles';
import {
	DateToFromFilterModel,
	FilterModel,
	FilterType,
	SelectFilterModelMultiple,
	SelectFilterModelSimple,
	datePickerFormat,
} from 'features/common/types';
import { formatDateToShowUser, getFilterModalTitle } from 'features/common/helpers';
import { isValidDate } from 'features/quotation/helpers';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

interface CrossesFiltersV2Props {
	filters: FilterModel[];
	handleAppliedFilters: (filters: FilterModel[]) => void;
	preAppliedFilters?: FilterModel[];
	quickFilterKeys?: string[];
}

const useStyles = makeStyles((theme) => ({
	chipWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
	},
}));

export const CrossesFiltersV2 = ({
	filters,
	handleAppliedFilters,
	preAppliedFilters,
	quickFilterKeys = [],
}: CrossesFiltersV2Props): JSX.Element => {
	const classes = useStyles();
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>(preAppliedFilters ? [...preAppliedFilters] : []);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {
		setFilterApplied(preAppliedFilters || []);
	}, [preAppliedFilters]);

	const handleApplyFilter = (filter: FilterModel) => {
		const newFilters = [...filterApplied];
		const indexInFilters = newFilters.findIndex((x) => x.key === filter.key);
		indexInFilters !== -1 ? (newFilters[indexInFilters] = filter) : newFilters.push(filter);
		setFilterApplied(newFilters);
		handleAppliedFilters(newFilters);
	};

	const handleDeleteFilter = (filter: FilterModel) => {
		const newFilters = [...filterApplied];
		const indexInFilters = newFilters.findIndex((x) => x.key === filter.key);
		if (indexInFilters !== -1) {
			newFilters.splice(indexInFilters, 1);
			setFilterApplied(newFilters);
			handleAppliedFilters(newFilters);
		}
	};

	const handleDeleteAllFilter = () => {
		setFilterApplied([]);
		handleAppliedFilters([]);
	};

	const handleValueChange = (filter: FilterModel, value: any, secondValue?: string) => {
		if (filter instanceof DateToFromFilterModel) {
			const hasValue = Boolean(value) || Boolean(secondValue);
			if (!hasValue) {
				handleDeleteFilter(filter);
				return;
			}
			const nextFilter = new DateToFromFilterModel(filter.label, filter.key, filter.secondKey, value, secondValue);
			handleApplyFilter(nextFilter);
			return;
		}

		if (Array.isArray(value)) {
			if (value.length === 0) {
				handleDeleteFilter(filter);
				return;
			}
		} else if (value === '' || value === null || value === undefined) {
			handleDeleteFilter(filter);
			return;
		}

		handleApplyFilter({ ...filter, value });
	};

	const getChipLabel = (filter: FilterModel): string => {
		switch (filter.type) {
			case FilterType.STRING:
			case FilterType.NUMERIC:
				return `${getFilterModalTitle(filter)} ${filter.value}`;
			case FilterType.LIST:
				if (filter instanceof SelectFilterModelMultiple) {
					const valueToRender = Array.isArray(filter.value)
						? [...filter.value].map((id) => filter.data.find((x) => x.id === id)?.name)
						: [];
					return `${filter.label} igual a ${valueToRender.join(',')}`;
				}
				if (filter instanceof SelectFilterModelSimple) {
					return `${filter.label} igual a ${filter.data.find((x) => x.id === filter.value)?.name}`;
				}
				return `${filter.label} igual a ${filter.value}`;
			case FilterType.BOOLEAN:
				return `${filter.label} igual a ${filter.value ? 'Si' : 'No'}`;
			case FilterType.DATE:
				if (typeof filter.value === 'string') {
					return `${filter.label} igual a ${formatDateToShowUser(filter.value)}`;
				}
				return filter.label;
			case FilterType.DATEFROMTO:
				if (filter instanceof DateToFromFilterModel && filter.value && filter.secondValue) {
					return `${filter.label} desde ${formatDateToShowUser(filter.value)} hasta ${formatDateToShowUser(
						filter.secondValue,
					)}`;
				}
				return filter.label;
			default:
				return `${filter.label} igual a ${filter.value}`;
		}
	};

	const activeCount = useMemo(() => filterApplied.length, [filterApplied.length]);
	const hasCustomQuickFilters = quickFilterKeys.length > 0;
	const quickFilterSet = useMemo(() => new Set(quickFilterKeys), [quickFilterKeys]);
	const quickFilters = useMemo(
		() => (hasCustomQuickFilters ? filters.filter((filter) => quickFilterSet.has(filter.key)) : filters),
		[filters, quickFilterSet, hasCustomQuickFilters],
	);
	const expandedFilters = useMemo(
		() => (hasCustomQuickFilters ? filters.filter((filter) => !quickFilterSet.has(filter.key)) : []),
		[filters, quickFilterSet, hasCustomQuickFilters],
	);

	const getAppliedFilter = (filter: FilterModel): FilterModel => {
		const applied = filterApplied.find((item) => item.key === filter.key);
		return applied || filter;
	};

	const parseDateValue = (value?: string) => {
		if (!value) return null;
		const date = new Date(`${value}T00:00:00`);
		return isValidDate(date) ? date : null;
	};

	const formatDateValue = (dateValue: Date | null) => {
		if (!dateValue || !isValidDate(dateValue)) return '';
		return dateValue.toLocaleString('sv', { timeZoneName: 'short' }).split(' ')[0];
	};

	const renderInlineFilter = (filter: FilterModel) => {
		const applied = getAppliedFilter(filter);
		const label = filter.label;

		if (filter.type === FilterType.BOOLEAN) {
			return (
				<FormControlLabel
					control={
						<Switch
							checked={Boolean(applied.value)}
							onChange={(event) => handleValueChange(filter, event.target.checked ? true : null)}
							color="primary"
						/>
					}
					label={label}
				/>
			);
		}

		if (filter.type === FilterType.STRING) {
			return (
				<TextField
					fullWidth
					size="small"
					label={label}
					value={applied.value ?? ''}
					onChange={(event) => handleValueChange(filter, event.target.value)}
				/>
			);
		}

		if (filter.type === FilterType.NUMERIC) {
			return (
				<TextField
					fullWidth
					size="small"
					label={label}
					type="number"
					value={applied.value ?? ''}
					onChange={(event) => handleValueChange(filter, event.target.value)}
				/>
			);
		}

		if (filter.type === FilterType.LIST) {
			if (filter instanceof SelectFilterModelMultiple) {
				const value = (applied.value as (string | number)[]) || [];
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{label}</InputLabel>
						<Select
							multiple
							value={value}
							label={label}
							onChange={(event) => handleValueChange(filter, event.target.value)}
						>
							{filter.data.map((item) => (
								<MenuItem key={item.id} value={item.id}>
									{item.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			}
			if (filter instanceof SelectFilterModelSimple) {
				return (
					<FormControl fullWidth size="small">
						<InputLabel>{label}</InputLabel>
						<Select
							value={applied.value ?? ''}
							label={label}
							onChange={(event) => handleValueChange(filter, event.target.value)}
						>
							<MenuItem value="">
								<em>Todos</em>
							</MenuItem>
							{filter.data.map((item) => (
								<MenuItem key={item.id} value={item.id}>
									{item.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			}
		}

		if (filter.type === FilterType.DATE) {
			return (
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={label}
						inputFormat={datePickerFormat}
						value={parseDateValue(applied.value as string)}
						onChange={(value) => handleValueChange(filter, formatDateValue(value))}
						renderInput={(params) => <TextField {...params} fullWidth size="small" />}
					/>
				</LocalizationProvider>
			);
		}

		if (filter.type === FilterType.DATEFROMTO && filter instanceof DateToFromFilterModel) {
			const appliedDateFilter = applied as DateToFromFilterModel;
			return (
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<Grid container spacing={1}>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label={`${filter.label} desde`}
								inputFormat={datePickerFormat}
								value={parseDateValue(appliedDateFilter.value)}
								onChange={(value) => handleValueChange(filter, formatDateValue(value), appliedDateFilter.secondValue)}
								renderInput={(params) => <TextField {...params} fullWidth size="small" />}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<DatePicker
								label={`${filter.label} hasta`}
								inputFormat={datePickerFormat}
								value={parseDateValue(appliedDateFilter.secondValue)}
								onChange={(value) => handleValueChange(filter, appliedDateFilter.value, formatDateValue(value))}
								renderInput={(params) => <TextField {...params} fullWidth size="small" />}
							/>
						</Grid>
					</Grid>
				</LocalizationProvider>
			);
		}

		return null;
	};

	const getGridSize = (filter: FilterModel) => {
		if (filter.type === FilterType.DATEFROMTO) {
			return { xs: 12, md: 6 };
		}
		if (filter.type === FilterType.DATE) {
			return { xs: 12, md: 6 };
		}
		return { xs: 12, sm: 6, md: 3 };
	};

	const canExpand = expandedFilters.length > 0;

	return (
		<Paper elevation={2} sx={{ p: 2, mb: 2, mt: 2 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Badge badgeContent={activeCount} color="primary">
						<FilterListIcon color="action" />
					</Badge>
					<Typography variant="subtitle1" fontWeight={500} component="h2">
						Filtros:
					</Typography>
				</Box>

				<Box display="flex" gap={1}>
					<Tooltip title="Limpiar todos los filtros">
						<Button
							size="small"
							startIcon={<ClearIcon />}
							onClick={handleDeleteAllFilter}
							variant="outlined"
							color="error"
						>
							Limpiar
						</Button>
					</Tooltip>
					{canExpand && (
						<Tooltip title={expanded ? 'Ocultar filtros' : 'Mostrar más filtros'}>
							<Button
								size="small"
								endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setExpanded(!expanded)}
								variant="outlined"
								color="primary"
								sx={{
									fontWeight: 500,
									borderWidth: 1.5,
									'&:hover': {
										borderWidth: 1.5,
										backgroundColor: 'primary.light',
										color: 'primary.contrastText',
									},
								}}
							>
								{expanded ? 'Menos filtros' : 'Más filtros'}
							</Button>
						</Tooltip>
					)}
				</Box>
			</Box>

			{activeCount > 0 && (
				<Box className={classes.chipWrap} mb={1}>
					{filterApplied.map((filter) => (
						<Chip
							key={`chip-${filter.key}`}
							label={getChipLabel(filter)}
							onDelete={() => handleDeleteFilter(filter)}
							color="primary"
							variant="outlined"
							size="small"
							sx={{ mr: 1, mb: 0.5 }}
						/>
					))}
				</Box>
			)}

			<Grid container spacing={1.5} sx={{ mb: 1 }}>
				{quickFilters.map((filter) => {
					const size = getGridSize(filter);
					return (
						<Grid item key={`quick-${filter.key}`} xs={size.xs} sm={size.sm} md={size.md}>
							{renderInlineFilter(filter)}
						</Grid>
					);
				})}
			</Grid>

			{canExpand && (
				<Collapse in={expanded}>
					<Divider sx={{ mb: 1 }} />
					<Grid container spacing={1.5}>
						{expandedFilters.map((filter) => {
							const size = getGridSize(filter);
							return (
								<Grid item key={`filter-${filter.key}`} xs={size.xs} sm={size.sm} md={size.md}>
									{renderInlineFilter(filter)}
								</Grid>
							);
						})}
					</Grid>
				</Collapse>
			)}
		</Paper>
	);
};
